import { isProjectWorkspaceReady } from '@/lib/projects/setup';
import { canPreserveWorkspaceThread } from '@/lib/slack-agent/session-ui';
import type { SerializedProject } from '@/types/projects';

type ProjectNavigationTarget = Pick<
  SerializedProject,
  'onboarding_completed' | 'onboarding_job_id' | 'setup_status'
>;

export function getProjectSwitchDestination(project: ProjectNavigationTarget): string {
  if (!isProjectWorkspaceReady(project)) {
    return '/app/workspace?setup=true';
  }

  return '/app/workspace?new_chat=true';
}

/**
 * Manual switches must reload the current page, including detail and incomplete-project routes.
 * A resource absent from the selected project remains unavailable there; navigation stays manual.
 */
export function reloadCurrentProjectPage(project: Pick<SerializedProject, 'id' | 'workspace_id'>) {
  const url = new URL(window.location.href);
  const keepWorkspaceThread =
    url.pathname === '/app/workspace' &&
    canPreserveWorkspaceThread(url.search, project.workspace_id);

  for (const key of ['projectId', 'project_id']) {
    if (url.searchParams.has(key)) url.searchParams.set(key, project.id);
  }
  // These identifiers and drafts belong to the project being left. Keep page/view/date filters.
  for (const key of [
    'canvas',
    'preview',
    'issue',
    'approvalId',
    'taskId',
    'promptId',
    'prompt_id',
    'prompt_ids',
    'prompt_group',
    'prompt_group_id',
    'prompt_group_origin',
    'prompt_subtopic_id',
    'topic_filter',
    'jobId',
    'job_id',
    'jobIds',
    'job_ids',
    'crawl',
    'crawlId',
    'ids',
    'cursor',
    'cursor_id',
    'evidence_item',
    'open_answer',
    'returnTo',
    'ctx',
    'question',
    'composer_draft',
    'new_project',
    'setup',
    'onboarding_chat',
    'reset',
  ])
    url.searchParams.delete(key);
  if (!keepWorkspaceThread) {
    for (const key of ['session', 'sessionId', 'session_scope', 'workspace_id'])
      url.searchParams.delete(key);
  }
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  window.location.reload();
}
